import { FEATURE_FLAGS } from 'constants/chownow';
import {
  LOCATION_CUT_OFF_DESTINATIONS,
  LOCATION_PATH,
  LOCATION_PATHS_TO_CUT_OFF_ON_LOCATION_SELECT,
  LOCATION_PATHS_TO_EXCLUDE_FROM_CUT_OFFS,
  CUBOH_COMPANY_PREFIX,
} from 'constants/path';
import { canSeeMenuManagement, canSeeMenuMapping } from 'scopes/cubohUserMetaHelpers';
import { canSeePosMenuSettings } from 'scopes/posHelpers';
import {
  canSeeAggregationPage,
  canSeeCampaignSettingsPage,
  canSeeCampaignsPage,
  canSeeOneTimeCampaignsPage,
  hasFeatureFlag,
  hasLocationScope,
} from './helpers';
import { RESTAURANT_INTEGRATION_POS, RESTAURANT_WEBSITE_ACCESS } from './scopes';

export const ACCOUNTING_DATA = 'accounting_data';
export const ADD_ONS = 'add_ons';
export const ANALYTICS = 'analytics';
export const AUTO_CAMPAIGNS = 'auto_campaigns';
export const CN_POS_INTEGRATIONS = 'pos_integrations';
export const CUBOH_INTEGRATIONS = 'cuboh_integrations';
export const CUSTOMER_DATA = 'customer_data';
export const CUSTOMER_DETAILS = 'customer_details';
export const CUSTOMIZATIONS = 'customizations';
export const DIRECT_ORDERS = 'direct_orders';
export const DIRECT_SETTINGS = 'direct_settings';
export const EMAIL_NOTIFICATIONS = 'email_notifications';
export const EMAILED = 'emailed';
export const HQ_DETAILS = 'hq_details';
export const HQ_DETAILS_EDIT = 'hq_details_edit';
export const HOME = 'home';
export const INTEGRATIONS = 'integrations';
export const ITEM_AVAILABILITY = 'item_availability';
export const ITEM_REPORT = 'item_report';
export const LOCATION_HOME = 'location_home';
export const LOCATIONS = 'locations';
export const MANAGE_MENU = 'manage_menu';
export const MANAGE_PHOTOS = 'manage_photos';
export const MANAGE_REWARDS = 'manage_rewards';
export const MARKETING = 'marketing';
export const MAPPING = 'mapping';
export const MENU = 'menu';
export const MODIFIER_REPORT = 'modifier_report';
export const ONE_TIME_CAMPAIGNS = 'one_time_campaigns';
export const ORDER_HISTORY = 'order_history';
export const ORDER_DETAILS = 'order_details';
export const ORDER_EDIT_DATA = 'order_edit_data';
export const ORDER_CANCELLATIONS = 'order_cancellations';
export const ORDER_CONTROL_DATA = 'order_control_data';
export const ORDER_SOURCE = 'order_source';
export const ORDERS = 'orders';
export const ORDER_OVERVIEW = 'order_overview';
export const ORDERS_CONTROL = 'orders_control';
export const OVERVIEW = 'overview';
export const PROGRESS = 'progress';
export const PAYMENT_PROCESSING = 'payment_processing';
export const CATERING_SETTINGS = 'catering_settings';
export const POS_ERRORS = 'pos_errors';
export const POS_MENU_SETTINGS = 'pos_menu_settings';
export const PROMO_DATA = 'promo_data';
export const PROMO_SETTINGS = 'promo_settings';
export const PUSH_NOTIFICATIONS = 'push_notifications';
export const REPORTS = 'reports';
export const REPORTS_LOCATION_BASED = 'reports_location_based';
export const TEMP_CLOSURES = 'temp_closures';
export const THIRD_PARTY_ORDERS = 'third_party_orders';
export const THIRD_PARTY_ORDERS_LOCATION_BASED = 'third_party_orders_location_based';
export const USERS = 'users';
export const WEBSITE_ACCESS = 'website_access';
export const CAMPAIGN_INSIGHTS = 'campaign_insights';
export const CAMPAIGN_SETTINGS = 'campaign_settings';
export const CHANNEL_MANAGER = 'channel_manager';
export const CHANNEL_INSIGHTS = 'channel_insights';

// Empty list --> no requirements
const NAV_CONDITIONS = {
  [ACCOUNTING_DATA]: [scopeDetails => scopeDetails.hasChowNowUser],
  [ADD_ONS]: [scopeDetails => scopeDetails.isInternalUser],
  // With the new Location Home, Analytics is hidden for every plan except Cuboh-only users
  [ANALYTICS]: [
    (scopeDetails, featureFlags) =>
      !hasFeatureFlag(FEATURE_FLAGS.rpDashAgent)(scopeDetails, featureFlags) ||
      !!scopeDetails.isUserCubohOnly,
  ],
  [CHANNEL_INSIGHTS]: [scopeDetails => canSeeAggregationPage(scopeDetails)],
  [CN_POS_INTEGRATIONS]: [scopeDetails => scopeDetails.hasChowNowUser],
  [CUBOH_INTEGRATIONS]: [
    scopeDetails =>
      canSeeAggregationPage(scopeDetails) ||
      canSeeAggregationPage(scopeDetails, RESTAURANT_INTEGRATION_POS) ||
      // Toast and legacy Square are reached from this page, so match CN_POS_INTEGRATIONS
      scopeDetails.hasChowNowUser,
  ],
  [CUSTOMER_DATA]: [scopeDetails => scopeDetails.hasChowNowUser],
  [CUSTOMER_DETAILS]: [scopeDetails => scopeDetails.hasChowNowUser],
  [CUSTOMIZATIONS]: [scopeDetails => scopeDetails.hasChowNowUser],
  [DIRECT_ORDERS]: [scopeDetails => scopeDetails.hasChowNowUser],
  [DIRECT_SETTINGS]: [scopeDetails => scopeDetails.hasChowNowUser],
  [EMAIL_NOTIFICATIONS]: [scopeDetails => scopeDetails.hasChowNowUser],
  [HOME]: [],
  [HQ_DETAILS]: [scopeDetails => scopeDetails.hasChowNowUser],
  [HQ_DETAILS_EDIT]: [
    scopeDetails => scopeDetails.hasChowNowUser,
    scopeDetails => !scopeDetails.isRegionalAdminUser,
  ],
  [INTEGRATIONS]: [],
  [ITEM_AVAILABILITY]: [scopeDetails => scopeDetails.isLegacyMenuEditor],
  [ITEM_REPORT]: [],
  [LOCATION_HOME]: [
    hasFeatureFlag(FEATURE_FLAGS.rpDashAgent),
    scopeDetails => !scopeDetails.isUserCubohOnly,
  ],
  [LOCATIONS]: [scopeDetails => scopeDetails.hasChowNowUser],
  [MANAGE_MENU]: [scopeDetails => canSeeMenuManagement(scopeDetails)],
  [MANAGE_PHOTOS]: [scopeDetails => scopeDetails.hasChowNowUser],
  [MANAGE_REWARDS]: [scopeDetails => scopeDetails.hasChowNowUser],
  [MARKETING]: [],
  [MAPPING]: [scopeDetails => canSeeMenuMapping(scopeDetails)],
  [MENU]: [],
  [MODIFIER_REPORT]: [],
  [AUTO_CAMPAIGNS]: [scopeDetails => canSeeCampaignsPage(scopeDetails)],
  [ONE_TIME_CAMPAIGNS]: [scopeDetails => canSeeOneTimeCampaignsPage(scopeDetails)],
  [ORDER_CANCELLATIONS]: [scopeDetails => canSeeAggregationPage(scopeDetails)],
  [ORDER_CONTROL_DATA]: [scopeDetails => canSeeAggregationPage(scopeDetails)],
  [ORDER_DETAILS]: [scopeDetails => !!scopeDetails.hasChowNowUser],
  [ORDER_HISTORY]: [scopeDetails => canSeeAggregationPage(scopeDetails)],
  [ORDER_EDIT_DATA]: [scopeDetails => canSeeAggregationPage(scopeDetails)],
  [ORDER_SOURCE]: [scopeDetails => scopeDetails.hasChowNowUser],
  [ORDERS]: [],
  [ORDERS_CONTROL]: [scopeDetails => canSeeAggregationPage(scopeDetails)],
  [OVERVIEW]: [scopeDetails => canSeeAggregationPage(scopeDetails)],
  [PAYMENT_PROCESSING]: [scopeDetails => scopeDetails.hasChowNowUser],
  [POS_ERRORS]: [
    scopeDetails =>
      canSeeAggregationPage(scopeDetails) ||
      canSeeAggregationPage(scopeDetails, RESTAURANT_INTEGRATION_POS),
  ],
  [POS_MENU_SETTINGS]: [hasFeatureFlag(FEATURE_FLAGS.posMenuSettings), canSeePosMenuSettings],
  [PROGRESS]: [scopeDetails => scopeDetails.hasChowNowUser],
  [PROMO_DATA]: [scopeDetails => scopeDetails.hasChowNowUser],
  [PROMO_SETTINGS]: [scopeDetails => scopeDetails.hasChowNowUser],
  [PUSH_NOTIFICATIONS]: [scopeDetails => scopeDetails.isInternalUser],
  [REPORTS]: [scopeDetails => scopeDetails.isUserCubohOnly],
  [REPORTS_LOCATION_BASED]: [scopeDetails => !scopeDetails.isUserCubohOnly],
  [TEMP_CLOSURES]: [scopeDetails => scopeDetails.hasChowNowUser],
  [THIRD_PARTY_ORDERS]: [scopeDetails => scopeDetails.isUserCubohAndChowNow],
  [THIRD_PARTY_ORDERS_LOCATION_BASED]: [scopeDetails => canSeeAggregationPage(scopeDetails)],
  [USERS]: [
    scopeDetails => scopeDetails.hasChowNowUser,
    scopeDetails => !scopeDetails.isRegionalAdminUser,
  ],
  [WEBSITE_ACCESS]: [hasLocationScope(RESTAURANT_WEBSITE_ACCESS)],
  [CAMPAIGN_INSIGHTS]: [scopeDetails => canSeeCampaignSettingsPage(scopeDetails)],
  [CAMPAIGN_SETTINGS]: [scopeDetails => canSeeCampaignSettingsPage(scopeDetails)],
  [CHANNEL_MANAGER]: [],
};

export const canAccessNavItem = ({ navKey, scopeDetails, featureFlags }) => {
  const navConditions = NAV_CONDITIONS[navKey];

  // also handles if no navKey is passed
  if (!navConditions) {
    return true;
  }

  return navConditions.every(condition => condition(scopeDetails, featureFlags));
};

export const getRestaurantPath = (isCubohOnly, id) =>
  `${LOCATION_PATH}/${isCubohOnly ? CUBOH_COMPANY_PREFIX : ''}${id ?? ''}`;

export const getUpdatedLocationPath = ({ pathname, id, prevId, isUserCubohOnly }) => {
  const matchingPathToCutOff = LOCATION_PATHS_TO_CUT_OFF_ON_LOCATION_SELECT.find(path =>
    pathname.includes(path),
  );

  const matchingPathToExcludeFromCutOff = LOCATION_PATHS_TO_EXCLUDE_FROM_CUT_OFFS.find(path =>
    pathname.includes(path),
  );
  const shouldCutOffPath = matchingPathToCutOff && !matchingPathToExcludeFromCutOff;
  const restaurantPath = getRestaurantPath(isUserCubohOnly, id);

  const cutOffDestination =
    LOCATION_CUT_OFF_DESTINATIONS[matchingPathToCutOff] ?? matchingPathToCutOff;

  const newPathname = shouldCutOffPath
    ? `${restaurantPath}${cutOffDestination}`
    : pathname.replace(`${prevId}`, `${isUserCubohOnly ? CUBOH_COMPANY_PREFIX : ''}${id}`);

  return newPathname;
};
